import React, { FC, useCallback, useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';

import { faroLogger } from '../../../observability/faro-logger';

const APP_VERSION_KEY = 'agent_app_version_seen'; // legacy
const STATE_KEY = 'agent_whats_new_state';
const SESSION_KEY = 'agent_whats_new_session_shown';
const DEMO_SEEN_KEY = 'agent_demo_seen';
const CURRENT_VERSION = process.env.REACT_APP_VERSION || '0.0.0';
// Nombre d ouvertures auto apres une nouvelle version
const AUTO_SHOW_LIMIT = 5;

interface StoredState {
  version: string;
  dismissals: number;
}

function readState(): StoredState {
  try {
    const raw = localStorage.getItem(STATE_KEY);
    if (raw) {
      const parsed = JSON.parse(raw);
      if (
        typeof parsed?.version === 'string' &&
        typeof parsed?.dismissals === 'number'
      ) {
        return parsed;
      }
    }
    // Compat ancien systeme : si l ancienne cle indique deja vu pour la
    // version courante, on considere comme deja dismisse 5 fois.
    const legacy = localStorage.getItem(APP_VERSION_KEY);
    if (legacy === CURRENT_VERSION) {
      return { version: CURRENT_VERSION, dismissals: AUTO_SHOW_LIMIT };
    }
  } catch {
    /* ignore */
  }
  return { version: CURRENT_VERSION, dismissals: 0 };
}

function writeState(state: StoredState): void {
  try {
    localStorage.setItem(STATE_KEY, JSON.stringify(state));
  } catch {
    /* ignore */
  }
}

interface Highlight {
  icon: string;
  title: string;
  body: string;
}

const HIGHLIGHTS: Highlight[] = [
  {
    icon: '☀️',
    title: 'Mode clair pour l\'extérieur',
    body: 'Bascule clair/sombre via le bouton 🌙/☀️ dans le bandeau du haut. Le mode clair est plus lisible en plein soleil.',
  },
  {
    icon: '📘',
    title: 'Livret de bord enrichi',
    body: 'Toutes les consignes (contacts, règles, produits, véhicule…) accessibles depuis le sommaire. Validation de lecture par section.',
  },
  {
    icon: '📅',
    title: 'Planning du jour sur l\'accueil',
    body: 'Liste cliquable des clients de la journée directement sur la page d\'accueil. Tap = fiche client. Vert = saisi, rouge = à faire.',
  },
  {
    icon: '⏰',
    title: 'Rappel quotidien',
    body: 'Bandeau sur la home rappelant de saisir avant de quitter chaque chantier et de prendre la photo des plaques.',
  },
];

/* ============================================================
   Hook expose pour piloter le popup depuis l exterieur (badge).
   ============================================================ */
export function useWhatsNew(): {
  hasUnseen: boolean;
  open: boolean;
  show: () => void;
  close: () => void;
} {
  const [state, setState] = useState<StoredState>(() => {
    const s = readState();
    if (s.version !== CURRENT_VERSION) {
      // Nouvelle version detectee : reset le compteur
      const fresh: StoredState = { version: CURRENT_VERSION, dismissals: 0 };
      writeState(fresh);
      return fresh;
    }
    return s;
  });
  const [open, setOpen] = useState(false);

  const hasUnseen = state.dismissals < AUTO_SHOW_LIMIT;

  const show = useCallback((): void => {
    setOpen(true);
    faroLogger.info('whats_new_opened', {
      version: CURRENT_VERSION,
      source: 'badge',
      dismissals: state.dismissals,
    });
  }, [state.dismissals]);

  const close = useCallback((): void => {
    // Lire l etat frais depuis localStorage (l instance auto a peut-etre
    // deja incremente entre temps).
    const current = readState();
    const baseDismissals =
      current.version === CURRENT_VERSION ? current.dismissals : 0;
    const next: StoredState = {
      version: CURRENT_VERSION,
      dismissals: Math.min(AUTO_SHOW_LIMIT, baseDismissals + 1),
    };
    writeState(next);
    setState(next);
    setOpen(false);
    faroLogger.info('whats_new_dismissed', {
      version: CURRENT_VERSION,
      dismissals: next.dismissals,
      source: 'badge',
    });
  }, []);

  return { hasUnseen, open, show, close };
}

/* ============================================================
   Composant : peut etre auto (sans props) ou controle (open/onClose)
   ============================================================ */
interface Props {
  open?: boolean;
  onClose?: () => void;
}

const WhatsNewPopup: FC<Props> = ({ open: controlledOpen, onClose }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const [autoOpen, setAutoOpen] = useState(false);
  const isControlled = controlledOpen !== undefined;
  const open = isControlled ? !!controlledOpen : autoOpen;

  // Mode auto : ouvre a chaque app boot tant que dismissals < AUTO_SHOW_LIMIT.
  // sessionStorage evite de reouvrir si l user navigue dans la SPA.
  useEffect(() => {
    if (isControlled) {
      return;
    }
    let demoSeen: string | null = null;
    let alreadyShownThisSession: string | null = null;
    try {
      demoSeen = localStorage.getItem(DEMO_SEEN_KEY);
      alreadyShownThisSession = sessionStorage.getItem(SESSION_KEY);
    } catch {
      /* noop */
    }
    if (!demoSeen) {
      return;
    }
    if (alreadyShownThisSession) {
      return;
    }
    const state = readState();
    if (state.version === CURRENT_VERSION && state.dismissals >= AUTO_SHOW_LIMIT) {
      return;
    }
    if (location.pathname === '/demo') {
      return;
    }
    const t = setTimeout(() => {
      setAutoOpen(true);
      try {
        sessionStorage.setItem(SESSION_KEY, '1');
      } catch {
        /* noop */
      }
      faroLogger.info('whats_new_auto_opened', {
        version: CURRENT_VERSION,
        dismissals: state.version === CURRENT_VERSION ? state.dismissals : 0,
      });
    }, 350);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const close = (): void => {
    if (isControlled) {
      onClose?.();
    } else {
      const current = readState();
      const next: StoredState = {
        version: CURRENT_VERSION,
        dismissals:
          current.version === CURRENT_VERSION
            ? Math.min(AUTO_SHOW_LIMIT, current.dismissals + 1)
            : 1,
      };
      writeState(next);
      setAutoOpen(false);
      faroLogger.info('whats_new_dismissed', {
        version: CURRENT_VERSION,
        dismissals: next.dismissals,
        source: 'auto',
      });
    }
  };

  const goToDemo = (): void => {
    close();
    navigate('/demo');
    faroLogger.info('whats_new_open_demo', { version: CURRENT_VERSION });
  };

  if (!open) {
    return null;
  }

  return (
    <div
      onClick={close}
      style={{
        position: 'fixed',
        inset: 0,
        background: 'rgba(0,0,0,0.65)',
        zIndex: 1000,
        display: 'flex',
        alignItems: 'flex-end',
        justifyContent: 'center',
        animation: 'wn-fade 0.2s ease-out',
      }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          width: '100%',
          maxWidth: 480,
          maxHeight: '88vh',
          overflowY: 'auto',
          background: 'var(--navy-mid)',
          borderTopLeftRadius: 22,
          borderTopRightRadius: 22,
          padding: '22px 20px 26px',
          color: 'var(--text-primary)',
          fontFamily: 'inherit',
          boxShadow: '0 -16px 48px rgba(0,0,0,0.5)',
          animation: 'wn-slide 0.28s cubic-bezier(0.16, 1, 0.3, 1)',
        }}
      >
        <style>{`
          @keyframes wn-slide { from { transform: translateY(100%); } to { transform: translateY(0); } }
          @keyframes wn-fade { from { opacity: 0; } to { opacity: 1; } }
        `}</style>

        <div
          style={{
            display: 'flex',
            justifyContent: 'center',
            marginBottom: 14,
          }}
        >
          <div
            style={{
              width: 38,
              height: 4,
              background: 'var(--bg-card-strong)',
              borderRadius: 100,
            }}
          />
        </div>

        <div
          style={{
            fontSize: 10.5,
            fontWeight: 800,
            letterSpacing: '0.14em',
            textTransform: 'uppercase',
            color: '#31DCC8',
            textAlign: 'center',
            marginBottom: 6,
          }}
        >
          ✨ Nouvelle version {CURRENT_VERSION}
        </div>
        <h2
          style={{
            fontSize: 22,
            fontWeight: 900,
            letterSpacing: '-0.02em',
            textAlign: 'center',
            margin: '0 0 6px',
            color: '#fff',
          }}
        >
          Quoi de neuf ?
        </h2>
        <p
          style={{
            fontSize: 13,
            color: 'rgba(248,250,251,0.7)',
            textAlign: 'center',
            margin: '0 0 18px',
            lineHeight: 1.45,
          }}
        >
          L&rsquo;app a évolué pour te faciliter la vie sur le terrain.
        </p>

        <ul
          style={{
            listStyle: 'none',
            padding: 0,
            margin: '0 0 18px',
            display: 'flex',
            flexDirection: 'column',
            gap: 10,
          }}
        >
          {HIGHLIGHTS.map((h) => (
            <li
              key={h.title}
              style={{
                background: 'rgba(255,255,255,0.04)',
                border: '1px solid rgba(255,255,255,0.08)',
                borderLeft: '3px solid #31DCC8',
                borderRadius: 10,
                padding: '12px 14px',
                display: 'flex',
                gap: 12,
                alignItems: 'flex-start',
              }}
            >
              <span
                style={{
                  fontSize: 22,
                  flexShrink: 0,
                  lineHeight: 1,
                  marginTop: 1,
                }}
              >
                {h.icon}
              </span>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div
                  style={{
                    fontSize: 13.5,
                    fontWeight: 800,
                    color: '#fff',
                    marginBottom: 3,
                    letterSpacing: '-0.005em',
                  }}
                >
                  {h.title}
                </div>
                <div
                  style={{
                    fontSize: 12,
                    color: 'rgba(248,250,251,0.7)',
                    lineHeight: 1.45,
                  }}
                >
                  {h.body}
                </div>
              </div>
            </li>
          ))}
        </ul>

        <button
          type="button"
          onClick={goToDemo}
          style={{
            width: '100%',
            padding: '14px 16px',
            background: '#31DCC8',
            color: 'var(--navy)',
            border: 'none',
            borderRadius: 12,
            fontSize: 15,
            fontWeight: 800,
            cursor: 'pointer',
            fontFamily: 'inherit',
            boxShadow: '0 8px 24px rgba(49,220,200,0.3)',
            letterSpacing: '-0.005em',
          }}
        >
          Voir le tutoriel pas à pas
        </button>
        <button
          type="button"
          onClick={close}
          style={{
            width: '100%',
            padding: '12px 16px',
            background: 'transparent',
            color: 'rgba(248,250,251,0.6)',
            border: 'none',
            marginTop: 6,
            fontSize: 13,
            fontWeight: 600,
            cursor: 'pointer',
            fontFamily: 'inherit',
          }}
        >
          Plus tard
        </button>
      </div>
    </div>
  );
};

export default WhatsNewPopup;
